/**
 * The Same-Day arrival windows, as the whole of the date and time step.
 *
 * Rendered only from what the server said is offerable for today, right now
 * (`docs/adr/0004-same-day-availability-server-side-only.md`). This component
 * holds no cutoff times and does no time arithmetic against a clock — it cannot
 * decide that a window is closed, and it must not appear to. It does not even
 * ask: the answer arrives as a prop, because `StepTwo` needs the same answer to
 * offer the way in and two components asking the same uncached question could
 * disagree with each other on the same screen.
 *
 * It replaces the calendar and the fixed-time grid rather than sitting beside
 * them, and that is the point of the mode. The grid's slots come from a static
 * 8:00am-to-6:00pm list with no notion of "now", so on today it offers to arrive
 * at eight in the morning at three in the afternoon. The server's windows are
 * the only times that are actually available today, so on today they are the
 * only times that may be shown.
 *
 * Visually it is one card, not a stack of boxes: a header that owns the date and
 * the premium, then a divided list of the windows, then the way out. The earlier
 * arrangement — an amber alert, a red alert, a heading and a set of floating
 * pills, each its own rounded rectangle — gave four unrelated objects the same
 * weight and left the customer to work out which one was the decision.
 */

import { formatCents } from "@homero/pricing";
import { ArrowLeft, CalendarOff, Check, Clock3, Zap } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled, { keyframes } from "styled-components";
import { RootState } from "src";
import { clearSameDaySelection, exitSameDay, setSameDay } from "../../actions";
import { SameDayAnswer } from "../../lib/pricing-config";
import { computeBookingPrice } from "../../lib/price";

export interface SameDayWindowsProps {
  /** The server's answer for today, or null if it refused or the request failed. */
  answer: SameDayAnswer | null;
  /** Whether that answer is still in flight. */
  loading: boolean;
  /** Today, as the business reckons it, for the heading. */
  serviceDate: string;
}

export function SameDayWindows({ answer, loading, serviceDate }: SameDayWindowsProps) {
  const dispatch = useDispatch();
  const state = useSelector((s: RootState) => s.bookingReducer);
  const { sameDay, arrivalWindow } = state;

  /**
   * The surcharge for today, or null if the server did not give us one.
   *
   * Not `?? 0`. A missing surcharge is a config or request problem, not a free
   * upgrade: `withSameDayForDate(config, date, 0, type)` builds a perfectly
   * valid config that enables Same-Day at no cost, so the engine's refusal to
   * price Same-Day at zero surcharge never fires, and the customer is shown a
   * total with no Same-Day line while the server charges one with it. The server
   * no longer returns null for a cleaning type it sells, so reaching this means
   * something is genuinely wrong and the honest move is to not offer Same-Day.
   */
  const surcharge =
    typeof answer?.surchargePercent === "number" ? answer.surchargePercent : null;


  /**
   * What the upgrade costs, in money.
   *
   * Priced through `computeBookingPrice` with the choice taken rather than by
   * multiplying anything here — the surcharge applies to a base that add-ons,
   * frequency and the day's surge all move, so a local `base * percent` would
   * drift from the cart beside it. A percentage on its own is not a price; it is
   * homework.
   *
   * `isFlexible` is forced off in the hypothetical because the reducer forces it
   * off for real: the two are mutually exclusive and the engine throws outright
   * on a booking claiming both, so a preview that left the store's default
   * `isFlexible: true` standing is a `RangeError` thrown during render.
   */
  const surchargeCents = useMemo(
    () =>
      surcharge
        ? computeBookingPrice({
            ...state,
            sameDay: true,
            isFlexible: false,
            sameDaySurchargePercent: surcharge,
          }).sameDaySurchargeCents
        : 0,
    /**
     * Only the inputs the quote actually reads. The component subscribes to the
     * whole booking slice, so it re-renders on any action at all — and on every
     * poll tick — and without this each of those ran a full price computation to
     * read one field off it.
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      surcharge,
      state.types,
      state.bedrooms,
      state.bathrooms,
      state.addOns,
      state.occurance,
      state.cleanDate,
      state.codeDiscount,
    ]
  );

  const heading = formatToday(serviceDate);

  /**
   * Let go of a window that has closed since it was chosen.
   *
   * The answer is re-asked on a timer, so the list can shrink under a customer
   * who picked the 12:00 window at 11:58 and is still filling in their address
   * at 12:01. Without this their choice stays selected and priced, and the first
   * anyone hears of it is the server refusing the booking after the card has
   * been charged — the failure mode this whole flow is built to avoid.
   *
   * Cleared rather than re-pointed at another window: which window they want is
   * their decision, not one to make on their behalf while they are looking away.
   */
  const [expired, setExpired] = useState(false);
  const stillOffered =
    !sameDay ||
    Boolean(answer?.windows?.some((window) => window.id === arrivalWindow));
  useEffect(() => {
    if (loading) return;
    if (!stillOffered) {
      setExpired(true);
      dispatch(clearSameDaySelection());
      return;
    }
    /**
     * And cleared once they have chosen again.
     *
     * `setExpired(true)` had no counterpart, so "that window has just closed"
     * stayed on screen above the new, perfectly valid selection — a red warning
     * about something the customer has already dealt with, which reads as the
     * new choice being broken too.
     */
    if (arrivalWindow) setExpired(false);
  }, [loading, stillOffered, arrivalWindow, dispatch]);

  /**
   * A skeleton of the card it is about to become, rather than a line of text.
   *
   * The answer arrives over the network on a step the customer has just switched
   * into, so the alternative is a paragraph that is replaced a moment later by a
   * card of a completely different height — the heading under it jumps. The
   * spoken version stays a sentence: there is nothing to read in grey bars.
   */
  if (loading) {
    return (
      <Panel data-testid="same-day-panel">
        <Head>
          <Eyebrow>
            <Zap size={12} fill="currentColor" aria-hidden="true" />
            Same-day
          </Eyebrow>
          <Bar $width="58%" $height={21} />
          <Bar $width="76%" $height={13} $top={14} />
        </Head>
        <SkeletonList aria-hidden="true">
          {[0, 1, 2].map((row) => (
            <SkeletonRow key={row}>
              <Bar $width="22px" $height={22} $round />
              <SkeletonText>
                <Bar $width={`${52 - row * 6}%`} $height={14} />
                <Bar $width={`${34 - row * 4}%`} $height={11} $top={7} />
              </SkeletonText>
            </SkeletonRow>
          ))}
        </SkeletonList>
        <SrOnly role="status">Checking today's availability…</SrOnly>
      </Panel>
    );
  }

  /**
   * Closed is a state with something to say, not an empty screen.
   *
   * The customer got here by asking for today, so "nothing here" would read as a
   * broken page. This can also happen *while* they are choosing, if the last
   * cutoff passes between the answer arriving and them acting on it — the way
   * out has to be on screen either way, and here it is the only thing left to
   * do, so it is a button rather than the quiet link it is beside a live list.
   */
  if (!answer?.available || answer.windows.length === 0 || surcharge === null) {
    return (
      <Panel data-testid="same-day-panel">
        <Empty>
          <EmptyGlyph aria-hidden="true">
            <CalendarOff size={20} />
          </EmptyGlyph>
          <EmptyTitle>Today is fully booked</EmptyTitle>
          <EmptyBody>
            There are no arrival windows left for today. Pick another day and
            we'll be there.
          </EmptyBody>
          <PrimaryBack type="button" onClick={() => dispatch(exitSameDay())}>
            <ArrowLeft size={15} aria-hidden="true" /> Back to regular booking
          </PrimaryBack>
        </Empty>
      </Panel>
    );
  }

  /**
   * The windows, in the order of the clock.
   *
   * The server hands them back in the order an operator happened to configure
   * them (`packages/pricing/src/same-day.ts`), which for a list of times of day
   * is no order at all — an afternoon window above a morning one is a list the
   * customer has to sort in their head before they can read it. Sorting on
   * `startMinutes` is arithmetic on two numbers the server sent, not a reading
   * of any clock, so it stays inside this component's one rule: it still cannot
   * tell whether a window is open, only where it sits among the ones that are.
   *
   * Sorted rather than merely tagged, because the "Soonest" tag below is only
   * true of the first row once the rows are actually in order.
   */
  const windows = [...answer.windows].sort(
    (a, b) => a.startMinutes - b.startMinutes
  );
  const scarce = windows.length === 1;

  return (
    <Panel data-testid="same-day-panel">
      <Head>
        <Eyebrow>
          <Zap size={12} fill="currentColor" aria-hidden="true" />
          Same-day
        </Eyebrow>
        <TitleRow>
          {/*
           * "Today" first, because that is the whole of what the customer chose
           * and the one thing that makes this step different from the calendar
           * they were on a moment ago. The date follows it as confirmation.
           */}
          <Title>Today, {heading}</Title>
          {/*
           * The premium as a price, right-aligned against the date, the way an
           * option's cost sits against its name in any list worth choosing from.
           * No justification for it and no explanation of how it is configured —
           * the customer is deciding whether to pay it, not auditing it.
           */}
          {surchargeCents > 0 ? (
            <Fee>
              <b>+{formatCents(surchargeCents)}</b>
              <small>{surcharge}%</small>
            </Fee>
          ) : null}
        </TitleRow>
        {/*
         * What the card offers is a three-hour window, so what it has to say is
         * that a specific arrival time is not on the table. The line used to read
         * "Flexible arrival isn't available on same-day bookings", which is the
         * opposite of what is on screen underneath it: every option in the list
         * below *is* a flexible arrival. It was naming the internal Flexible Time
         * discount — a thing the customer has no word for here — and reading, to
         * anyone else, as a denial of the only thing being offered.
         */}
        <Sub>
          The option to arrive at a specific time isn't available for same-day
          bookings.
        </Sub>
      </Head>

      {/*
       * Full-bleed inside the card rather than another floating box, and amber
       * rather than red: nothing has gone wrong and the customer did nothing
       * wrong. A window they were looking at sold out, which is news.
       */}
      {expired ? (
        <Notice role="status">
          <Clock3 size={15} aria-hidden="true" />
          <span>That window has just closed. Pick another one below.</span>
        </Notice>
      ) : null}

      <ListLabel aria-hidden="true">
        <span>Arrival window</span>
        <Count>
          <Pulse aria-hidden="true" />
          {windows.length} left today
        </Count>
      </ListLabel>

      <Windows>
        {/* The group's accessible name. Hidden because ListLabel above already
            says as much to everyone reading the screen. */}
        <legend>Select an arrival window</legend>
        {windows.map((window, index) => {
          const checked = sameDay && arrivalWindow === window.id;
          const span = describeSpan(window.startMinutes, window.endMinutes);
          const tag = scarce
            ? "Last today"
            : index === 0
            ? "Soonest"
            : null;
          const describedBy = `sd-window-${slug(window.id)}`;
          return (
            <WindowRow
              key={window.id}
              $checked={checked}
              /* A small stagger, inline so the list does not mint a class per
                 row. Suppressed under reduced motion by the rule in WindowRow. */
              style={{ animationDelay: `${Math.min(index, 6) * 45}ms` }}
            >
              <input
                type="radio"
                /*
                  A real radio input rather than a styled div: the group gets
                  arrow-key navigation, an accessible name and a checked state
                  from the platform, none of which a click handler on a span has.
                */
                name="same-day-arrival-window"
                value={window.id}
                checked={checked}
                /*
                  Named explicitly, so the row can carry a duration and a tag
                  without either of them being read out as part of the option's
                  name. They are description, not identity, and they reach a
                  screen reader through `aria-describedby` below.
                */
                aria-label={window.label}
                aria-describedby={span || tag ? describedBy : undefined}
                onChange={() => {
                  // The apology stops once it has been acted on.
                  setExpired(false);
                  dispatch(setSameDay(window.id, window.label, surcharge));
                }}
              />
              <FocusRing aria-hidden="true" />
              <Mark $checked={checked} aria-hidden="true">
                <Check size={13} strokeWidth={3} />
              </Mark>
              <RowMain>
                <RowLabel $checked={checked}>{typesetWindowLabel(window.label)}</RowLabel>
                {span ? <RowMeta aria-hidden="true">{span}</RowMeta> : null}
              </RowMain>
              {tag ? (
                <Tag $tone={scarce ? "warn" : "brand"} aria-hidden="true">
                  {tag}
                </Tag>
              ) : null}
              {span || tag ? (
                <SrOnly as="span" id={describedBy}>
                  {[span, tag === "Soonest" ? "Soonest available today" : null,
                    tag === "Last today" ? "The last window available today" : null]
                    .filter(Boolean)
                    .join(". ")}
                </SrOnly>
              ) : null}
            </WindowRow>
          );
        })}
      </Windows>

      <Foot>
        <BackLink type="button" onClick={() => dispatch(exitSameDay())}>
          <ArrowLeft size={15} aria-hidden="true" /> Back to regular booking
        </BackLink>
        {/* Explains both the live dot in the header and the notice above: the
            list is inventory, and it only ever gets shorter. */}
        <FootNote>Windows close as the day goes on</FootNote>
      </Foot>
    </Panel>
  );
}

/**
 * "Thursday, June 5" from a `YYYY-MM-DD`.
 *
 * Built from the parts rather than `new Date(serviceDate)`, which parses a bare
 * date as UTC midnight and so prints the day before for anyone west of it —
 * naming the wrong day on the one screen whose entire subject is *today*.
 */
function formatToday(serviceDate: string): string {
  const [year, month, day] = serviceDate.split("-").map(Number);
  if (!year || !month || !day) return "today";
  return new Date(year, month - 1, day).toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
  });
}

/**
 * How long the window is, from the server's own minutes.
 *
 * Arithmetic on two numbers the server sent, not on a clock — the rule this
 * component lives under is that it may not work out whether a window is *open*,
 * and how wide a window is says nothing about that. It is worth showing because
 * "12:00 PM – 2:00 PM" makes the customer do the subtraction to find the one
 * number they actually care about: how long they have to be in.
 */
function describeSpan(startMinutes: number, endMinutes: number): string | null {
  const minutes = endMinutes - startMinutes;
  if (!Number.isFinite(minutes) || minutes <= 0) return null;
  if (minutes < 60) return `${minutes}-minute arrival window`;
  if (minutes % 60 === 0) return `${minutes / 60}-hour arrival window`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m arrival window`;
}

/**
 * A hyphen between two times is a typo; the character for a range is an en dash.
 *
 * Only ever applied to a hyphen with a space on each side, so an operator who
 * typed "12-2pm" keeps what they typed and only " - " — which is nothing but a
 * range separator — is corrected.
 *
 * Exported because `StepTwo` prints the chosen window back to the customer under
 * this card, and a label typeset in one place and not the other is two different
 * spellings of the same time on one screen.
 */
export function typesetWindowLabel(label: string): string {
  // Guarded because the caller in `StepTwo` reads the label off an untyped
  // slice, where "no window chosen" is undefined rather than "".
  return label ? label.replace(/ - /g, " – ") : label;
}

/** A window code, made safe to put in an `id` attribute. */
function slug(id: string): string {
  return id.replace(/[^a-zA-Z0-9_-]/g, "-");
}

/* -------------------------------------------------------------------------- */

const pulse = keyframes`
  0%   { transform: scale(0.6); opacity: 0.55; }
  70%  { transform: scale(1.9); opacity: 0; }
  100% { transform: scale(1.9); opacity: 0; }
`;

const shimmer = keyframes`
  0%   { background-position: 100% 0; }
  100% { background-position: -100% 0; }
`;

const riseIn = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to   { opacity: 1; transform: none; }
`;

/**
 * One card, and the only place the palette is declared.
 *
 * Custom properties rather than repeated hex, so the greys stay one family
 * instead of drifting into four near-identical ones — the tell of a screen
 * assembled from defaults. They are deliberately blue-cast to sit with the
 * funnel's indigo rather than against it.
 */
const Panel = styled.div`
  --sd-ink: #101322;
  --sd-ink-2: #575e75;
  --sd-ink-3: #8b92a8;
  --sd-line: #e7e9f3;
  --sd-brand: var(--blue-300);
  --sd-brand-tint: #f2f4ff;
  --sd-warn: #96590c;
  --sd-warn-tint: #fff7ec;
  --sd-warn-line: #f2dcb9;

  font-family: Poppins, -apple-system, BlinkMacSystemFont, "Helvetica Neue",
    sans-serif;
  color: var(--sd-ink);
  background: #fff;
  border: 1px solid var(--sd-line);
  border-radius: 18px;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(16, 19, 34, 0.04),
    0 14px 30px -20px rgba(16, 19, 34, 0.24);
`;

const Head = styled.header`
  padding: 18px 20px 16px;
  background: linear-gradient(180deg, #f8f9ff 0%, #ffffff 100%);
  border-bottom: 1px solid var(--sd-line);

  @media (max-width: 520px) {
    padding: 16px;
  }
`;

const Eyebrow = styled.p`
  display: flex;
  align-items: center;
  gap: 7px;
  margin: 0 0 9px;
  color: var(--sd-brand);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
`;

/**
 * A live signal, because the list underneath it is one: the windows narrow
 * through the day and this component re-asks on a timer. It is the quietest
 * honest way to say so.
 */
const Pulse = styled.span`
  position: relative;
  flex: 0 0 auto;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--sd-brand);

  &::after {
    content: "";
    position: absolute;
    inset: -2px;
    border-radius: 50%;
    border: 1px solid var(--sd-brand);
    animation: ${pulse} 2.4s ease-out infinite;
  }

  @media (prefers-reduced-motion: reduce) {
    &::after {
      animation: none;
      opacity: 0.3;
    }
  }
`;

const TitleRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px 16px;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 21px;
  font-weight: 600;
  line-height: 1.25;
  letter-spacing: -0.015em;

  @media (max-width: 520px) {
    font-size: 18px;
  }
`;

const Fee = styled.span`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  padding: 5px 11px;
  border: 1px solid var(--sd-line);
  border-radius: 999px;
  background: #fff;
  /* Money and clock times line up column-wise down the card. */
  font-variant-numeric: tabular-nums;

  b {
    font-size: 15px;
    font-weight: 600;
    letter-spacing: -0.01em;
  }

  small {
    font-size: 12px;
    color: var(--sd-ink-3);
  }
`;

const Sub = styled.p`
  margin: 9px 0 0;
  color: var(--sd-ink-2);
  font-size: 13px;
  line-height: 1.5;
`;

/**
 * `role="status"` rather than a plain paragraph: the selection it explains
 * disappeared without the customer touching anything, so the explanation has to
 * reach someone who is not looking at this part of the screen.
 */
const Notice = styled.p`
  display: flex;
  align-items: center;
  gap: 9px;
  margin: 0;
  padding: 11px 20px;
  background: var(--sd-warn-tint);
  border-bottom: 1px solid var(--sd-warn-line);
  color: var(--sd-warn);
  font-size: 13px;
  line-height: 1.45;

  > svg {
    flex: 0 0 auto;
  }
`;

const ListLabel = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 15px 20px 9px;
  color: var(--sd-ink-3);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;

  @media (max-width: 520px) {
    padding: 14px 16px 8px;
  }
`;

/**
 * How many are left, with the live dot beside it.
 *
 * The dot sits here rather than in the eyebrow because this is the number it is
 * about: the list is re-asked on a timer and only ever gets shorter. Up in the
 * eyebrow it was a second mark next to the bolt, saying nothing the bolt did not
 * already say.
 */
const Count = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-variant-numeric: tabular-nums;
`;

const Windows = styled.fieldset`
  display: block;
  margin: 0;
  padding: 0;
  border: 0;
  min-width: 0;

  legend {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
    border: 0;
  }
`;

/**
 * The ring the platform would have drawn, had the radio not been made invisible
 * so the whole row could be the control. Its own element rather than a
 * pseudo-element on the label, so it can be a sibling of the input and light up
 * on `:focus-visible` without `:has`.
 */
const FocusRing = styled.span`
  position: absolute;
  inset: 4px;
  border-radius: 12px;
  border: 2px solid var(--sd-brand);
  opacity: 0;
  pointer-events: none;
  transition: opacity 120ms ease;
`;

/**
 * A divided list rather than a set of floating pills.
 *
 * There are only ever a handful of windows and each is the whole of the
 * customer's time choice, so they get the width of the card and the weight of a
 * decision. Selection reads as a tinted row with a filled mark and a rail down
 * its left edge — drawn with an inset shadow so choosing one does not shove the
 * text sideways by a pixel.
 */
const WindowRow = styled.label<{ $checked: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  gap: 13px;
  padding: 14px 20px;
  cursor: pointer;
  user-select: none;
  background: ${(p) => (p.$checked ? "var(--sd-brand-tint)" : "#fff")};
  box-shadow: ${(p) =>
    p.$checked ? "inset 3px 0 0 0 var(--sd-brand)" : "inset 0 0 0 0 transparent"};
  transition: background-color 140ms ease, box-shadow 160ms ease;
  animation: ${riseIn} 260ms ease both;

  & + & {
    border-top: 1px solid var(--sd-line);
  }

  &:hover {
    background: ${(p) => (p.$checked ? "var(--sd-brand-tint)" : "#fafbff")};
  }

  &:active {
    background: ${(p) => (p.$checked ? "var(--sd-brand-tint)" : "#f4f6fd")};
  }

  input {
    /*
      Kept in the layout and merely invisible rather than display:none, which
      would take the radio out of the tab order and off the a11y tree with it.
    */
    position: absolute;
    opacity: 0;
    inset: 0;
    margin: 0;
    cursor: pointer;
  }

  input:focus-visible ~ ${FocusRing} {
    opacity: 1;
  }

  @media (max-width: 520px) {
    padding: 13px 16px;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    transition: none;
  }
`;

const Mark = styled.span<{ $checked: boolean }>`
  flex: 0 0 auto;
  display: grid;
  place-items: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 1.5px solid ${(p) => (p.$checked ? "var(--sd-brand)" : "#c9cee0")};
  background: ${(p) => (p.$checked ? "var(--sd-brand)" : "#fff")};
  color: #fff;
  transition: background-color 140ms ease, border-color 140ms ease;

  svg {
    opacity: ${(p) => (p.$checked ? 1 : 0)};
    transform: scale(${(p) => (p.$checked ? 1 : 0.5)});
    transition: opacity 140ms ease, transform 180ms cubic-bezier(0.2, 1.3, 0.4, 1);
  }

  ${WindowRow}:hover & {
    border-color: ${(p) => (p.$checked ? "var(--sd-brand)" : "#aab2cb")};
  }

  @media (prefers-reduced-motion: reduce) {
    svg {
      transition: none;
    }
  }
`;

const RowMain = styled.span`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1 1 auto;
`;

const RowLabel = styled.span<{ $checked: boolean }>`
  font-size: 15px;
  font-weight: ${(p) => (p.$checked ? 600 : 500)};
  letter-spacing: -0.005em;
  font-variant-numeric: tabular-nums;
  line-height: 1.35;
`;

const RowMeta = styled.span`
  color: var(--sd-ink-3);
  font-size: 12.5px;
  line-height: 1.35;
`;

const Tag = styled.span<{ $tone: "brand" | "warn" }>`
  flex: 0 0 auto;
  padding: 3px 8px;
  border-radius: 6px;
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  background: ${(p) =>
    p.$tone === "warn" ? "var(--sd-warn-tint)" : "var(--sd-brand-tint)"};
  color: ${(p) => (p.$tone === "warn" ? "var(--sd-warn)" : "var(--sd-brand)")};
  border: 1px solid
    ${(p) => (p.$tone === "warn" ? "var(--sd-warn-line)" : "#dde1ff")};
`;

const Foot = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px 12px;
  padding: 12px 20px;
  border-top: 1px solid var(--sd-line);
  background: #fcfcfe;

  @media (max-width: 520px) {
    padding: 12px 16px;
  }
`;

const BackLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  /* Pulled left by its own padding so the text still starts on the card's
     margin, while the hover surface is a comfortable target. */
  margin-left: -10px;
  padding: 7px 12px 7px 10px;
  border: 0;
  border-radius: 9px;
  background: none;
  color: var(--sd-ink-2);
  font-family: inherit;
  font-size: 13.5px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 140ms ease, color 140ms ease;

  svg {
    transition: transform 180ms ease;
  }

  &:hover {
    background: #eef0f8;
    color: var(--sd-ink);
  }

  &:hover svg {
    transform: translateX(-2px);
  }

  &:focus-visible {
    outline: 2px solid var(--sd-brand);
    outline-offset: 2px;
  }

  @media (prefers-reduced-motion: reduce) {
    svg,
    & {
      transition: none;
    }
  }
`;

const FootNote = styled.small`
  color: var(--sd-ink-3);
  font-size: 12.5px;
`;

/* --- closed ---------------------------------------------------------------- */

const Empty = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 40px 24px 34px;
`;

const EmptyGlyph = styled.span`
  display: grid;
  place-items: center;
  width: 44px;
  height: 44px;
  margin-bottom: 16px;
  border-radius: 50%;
  background: #f3f4fa;
  color: var(--sd-ink-3);
`;

const EmptyTitle = styled.p`
  margin: 0;
  font-size: 17px;
  font-weight: 600;
  letter-spacing: -0.01em;
`;

const EmptyBody = styled.p`
  max-width: 34ch;
  margin: 7px 0 22px;
  color: var(--sd-ink-2);
  font-size: 13.5px;
  line-height: 1.55;
`;

const PrimaryBack = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 11px 18px;
  border: 0;
  border-radius: 10px;
  background: var(--sd-brand);
  color: #fff;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 140ms ease, transform 140ms ease;

  svg {
    transition: transform 180ms ease;
  }

  &:hover {
    background: var(--blue-400);
  }

  &:hover svg {
    transform: translateX(-2px);
  }

  &:active {
    transform: translateY(1px);
  }

  &:focus-visible {
    outline: 2px solid var(--sd-brand);
    outline-offset: 3px;
  }

  @media (prefers-reduced-motion: reduce) {
    svg,
    & {
      transition: none;
    }
  }
`;

/* --- loading --------------------------------------------------------------- */

const Bar = styled.span<{
  $width: string;
  $height: number;
  $top?: number;
  $round?: boolean;
}>`
  display: block;
  width: ${(p) => p.$width};
  height: ${(p) => p.$height}px;
  margin-top: ${(p) => p.$top ?? 0}px;
  border-radius: ${(p) => (p.$round ? "50%" : "6px")};
  background: linear-gradient(90deg, #eef0f6 25%, #f7f8fc 50%, #eef0f6 75%);
  background-size: 200% 100%;
  animation: ${shimmer} 1.4s linear infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const SkeletonList = styled.div`
  display: block;
`;

const SkeletonRow = styled.div`
  display: flex;
  align-items: center;
  gap: 13px;
  padding: 15px 20px;

  & + & {
    border-top: 1px solid var(--sd-line);
  }

  @media (max-width: 520px) {
    padding: 14px 16px;
  }
`;

const SkeletonText = styled.div`
  flex: 1 1 auto;
  min-width: 0;
`;

const SrOnly = styled.p`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
`;
